import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LogOut, UserRound } from 'lucide-react'
import { useAuth } from '@/providers/AuthProvider'
import { clearLastLocation } from '@/lib/lastLocation'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

/**
 * Compact account chip for the nav bars: person icon + short display name
 * (first name, falling back to the email's local part). Clicking opens a menu
 * with the full identity, a profile link, and sign out.
 */
export function AccountMenu() {
  const navigate = useNavigate()
  const { email, user, logout } = useAuth()
  const [open, setOpen] = useState(false)

  const fullName = user?.fullName?.trim() || null
  const shortName = fullName?.split(/\s+/)[0] ?? email?.split('@')[0] ?? 'Account'

  function handleLogout() {
    setOpen(false)
    // Signing out deliberately forgets where you were: the next sign-in starts
    // at the landing page, not back on the last screen. Not awaited, so the
    // sign-out is never held up by it — and it must run BEFORE logout(), which
    // clears the token the request needs.
    void clearLastLocation()
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label='Account'
          title='Account'
          className='flex h-8 max-w-[10rem] shrink-0 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
        >
          <UserRound className='h-4 w-4 shrink-0' />
          <span className='hidden truncate sm:inline'>{shortName}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-64 p-0'>
        <div className='border-b px-4 py-3'>
          {fullName && <p className='truncate text-sm font-medium text-foreground'>{fullName}</p>}
          {email && (
            <p className='truncate text-xs text-muted-foreground' title={email}>
              {email}
            </p>
          )}
        </div>
        <div className='flex flex-col p-1'>
          <Link
            to='/profile'
            onClick={() => setOpen(false)}
            className='flex items-center gap-2 rounded-sm px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted'
          >
            <UserRound className='h-4 w-4 text-muted-foreground' />
            Profile
          </Link>
          <button
            onClick={handleLogout}
            className='flex items-center gap-2 rounded-sm px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted'
          >
            <LogOut className='h-4 w-4 text-muted-foreground' />
            Sign out
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
